import './default.scss';

import { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import PaidAlertModal from '@/resources/components/cart/paidAlertModal/PaidAlertModal';
import SendOrderAlert from '@/resources/components/cart/sendOrderAlert/SendOrderAlert';
import Shipment from '@/resources/components/cart/shipment/Shipment';
import StatusSteps from '@/resources/components/cart/statusSteps/StatusSteps';
import Breadcrumb from '@/resources/components/global/breadcrumb/Breadcrumb';
import CartPageLoad from '@/resources/components/loads/CartPageLoad';
import { useCartContext } from '@/resources/hooks/useCartContext';
import { usePageBreakpoints } from '@/resources/hooks/usePageBreakpoints';
import { useSmartTextColor } from '@/resources/hooks/useSmartTextColor';
import { useTextColor } from '@/resources/hooks/useTextColor';
import { useWebContext } from '@/resources/hooks/useWebContext';
import { ApplicationState } from '@/store';
import { IPaymentAlertAsks } from '@/store/ducks/payment/types';
import { IProductList } from '@/store/ducks/productLists/types';

import { canChangeOrder, canFinishOrder, OrderStatus } from '../../../../services/global';
import { ComboAddModal } from '../../product/default/combos/components/ComboAddModal';
import { ComboBottomSheet } from '../../product/default/combos/components/ComboBottomSheet';
import { ComboModal } from '../../product/default/combos/components/ComboModal';
import { ComboRemoveItemModal } from '../../product/default/combos/components/ComboRemoveItemModal';
import { AddMoreItems, CartContent, EmptyCart, FreeShippingSection } from './components';
import { useCartData, useCartEffects, useCartModals } from './hooks';
import { shouldShowStatusSteps } from './utils/cartUtils';

function Default() {
  const color = useSmartTextColor();
  const listColor = useSmartTextColor('#666');
  const { breakpoints } = usePageBreakpoints();
  const { configs, setShowIsCartBlockedModal, order, menuOpen } = useWebContext();
  const textColor = useTextColor(configs.color);
  const { min_order_amount } = useCartContext();

  const [shipmentStep, setShipmentStep] = useState<1 | 2 | 3 | 4>(1);
  const [showSendOrderAlert, setShowSendOrderAlert] = useState<boolean>(false);
  const [showCartDeliveryAlert, setShowCartDeliveryAlert] = useState<boolean>(false);

  const cartModals = useCartModals();
  const cartData = useCartData();

  const freeShippingProducts = useSelector<ApplicationState, IProductList>(
    (state) => state.productLists.freeShipping,
  );

  const paymentAlertAsks = useSelector<ApplicationState, IPaymentAlertAsks>(
    (state) => state.payment.asks,
  );

  useCartEffects({
    order,
    paymentAlertAsks,
    setShowCartDeliveryAlert,
  });

  useEffect(() => {
    if (!menuOpen && cartModals.comboBottomSheetOpen) {
      cartModals.closeComboBottomSheet();
    }
  }, [menuOpen, cartModals.comboBottomSheetOpen, cartModals.closeComboBottomSheet]);

  useEffect(() => {
    if (cartData.productVariants && Object.keys(cartData.productVariants).length > 0) {
      cartModals.openComboDetails();
    }
  }, [cartData.productVariants, cartModals.openComboDetails]);

  const shipmentCardStyle = {
    backgroundColor: configs.color,
    borderColor: configs.color,
    color: textColor,
  };

  const handleCloseSendOrderAlert = () => setShowSendOrderAlert(false);

  const handleFinishOrder = () => {
    const { isValid: orderCanBeChanged, showIsCartBlockedModal } = canChangeOrder({
      type: 'pedido',
      action: 'finalizado',
    });

    if (
      (order?.status !== OrderStatus.OPEN && order?.status !== OrderStatus.ABANDONED,
      order?.cart.products.some((item: any) => item.prescription_retention))
    ) {
      return true;
    }

    if (orderCanBeChanged && canFinishOrder(shipmentStep)) return true;

    if (showIsCartBlockedModal) setShowIsCartBlockedModal(true);

    return false;
  };

  const handleCloseComboModal = () => {
    cartModals.closeComboModal();
    cartData.clearProductVariants();
  };

  const handleCloseComboBottomSheet = () => {
    cartModals.closeComboBottomSheet();
    cartData.clearProductVariants();
  };

  return (
    <section className="Cart pb-3">
      <Container fluid>
        <Row className="justify-content-center">
          <Col {...breakpoints} className="breakpoint-page">
            <Breadcrumb page="cart" />
            {order === null ? (
              <CartPageLoad />
            ) : (
              <>
                {order && order.cart.products.length > 0 ? (
                  <>
                    {shouldShowStatusSteps(order) && (
                      <Row className="justify-content-center my-2">
                        <Col xs={12} md={10} lg={8}>
                          <StatusSteps order={order} />
                        </Col>
                      </Row>
                    )}

                    <Row>
                      <Col xs={12} md={8}>
                        <CartContent
                          shipmentCardStyle={shipmentCardStyle}
                          showCartDeliveryAlert={showCartDeliveryAlert}
                          order={order}
                          min_order_amount={min_order_amount}
                          combos={cartData.combos}
                          setEditProducts={cartData.setEditProducts}
                          setProductToCombo={cartData.setProductToCombo}
                        />

                        <AddMoreItems order={order} />
                      </Col>

                      <Col xs={12} md={4}>
                        <Shipment
                          order={order}
                          setShipmentStep={setShipmentStep}
                          handleFinishOrder={handleFinishOrder}
                          forwardedOrderAlert={setShowSendOrderAlert}
                        />
                      </Col>

                      <Col xs={12}>
                        <SendOrderAlert
                          show={showSendOrderAlert}
                          handleClose={handleCloseSendOrderAlert}
                          socialNetworkType={order.client.social_network_type}
                          clientIdentification={order.client.identification}
                        />
                      </Col>

                      <Col xs={12}>
                        <FreeShippingSection
                          freeShippingProducts={freeShippingProducts}
                          order={order}
                          listColor={listColor}
                        />
                        <PaidAlertModal />
                      </Col>
                    </Row>
                  </>
                ) : (
                  <EmptyCart color={color} />
                )}
              </>
            )}
          </Col>
        </Row>
      </Container>

      <ComboBottomSheet
        open={cartModals.comboBottomSheetOpen}
        product={cartData.productToCombo}
        variants={cartData.productVariants}
        onClose={handleCloseComboBottomSheet}
        editProducts={cartData.editProducts}
      />
      <ComboModal
        show={cartModals.comboModalOpen}
        onClose={handleCloseComboModal}
        product={cartData.productToCombo}
        variants={cartData.productVariants}
        editProducts={cartData.editProducts}
      />
      <ComboAddModal
        show={cartModals.showComboAddModal}
        onClose={cartModals.closeComboAddModal}
        product={cartModals.modalAddProduct}
      />
      <ComboRemoveItemModal
        show={cartModals.comboRemoveItemModalOpen}
        onClose={cartModals.closeComboRemoveModal}
        product={cartModals.modalRemoveProduct}
      />
    </section>
  );
}

export default Default;
